import type { SensorStatus, SensorType } from "@sitescene/types";

import { RefreshCcw05 } from "@untitledui/icons";

import { Button } from "@/components/ui/buttons/button";
import { Input } from "@/components/ui/input/input";
import { Select } from "@/components/ui/select/select";
import { Tooltip } from "@/components/ui/tooltip/tooltip";

import type { ShadowSensorEditDraft } from "../../../utils/location/location-map-utils";
import type { SensorAutoSaveStatus } from "../../../utils/sensor/sensor-auto-save";

import { isSensorEditDraftCoordinatesValid } from "../../../utils/location/location-map-utils";
import { hasAutoSaveEdits } from "../../../utils/shared/auto-save-display";
import { UseMapCenterButton } from "../map-pane/use-map-center-button";
import { EditScrollBody } from "./edit-scroll-body";

const SENSOR_STATUS_OPTIONS = [
	{ id: "active", label: "Active" },
	{ id: "warning", label: "Warning" },
	{ id: "offline", label: "Offline" },
] as const;

interface SensorEditFormProps {
	autoSaveStatus?: SensorAutoSaveStatus;
	canUndoSensorEdit: boolean;
	editDraft: ShadowSensorEditDraft;
	editTypeOptions: { id: string; label: string }[];
	editZoneOptions: { id: string; label: string }[];
	isEditDirty: boolean;
	isUpdatedSensorPending: boolean;
	/** Current main-map viewport center for “Use map center”. */
	mapViewportCenter?: [number, number] | null;
	onCancel: () => void;
	onDraftChange: (
		updater: (current: ShadowSensorEditDraft) => ShadowSensorEditDraft,
	) => void;
	onSave: () => void;
	onZoneChange: (zoneId: string) => void;
	zoneMoveHint?: string;
}

const getLatitudeError = (latitude: string): string | undefined => {
	if (!latitude.trim()) return undefined;

	const value = parseFloat(latitude);
	if (Number.isNaN(value)) return "Must be a valid number";
	if (value < -90 || value > 90) return "Latitude must be between -90 and 90";
	return undefined;
};

const getLongitudeError = (longitude: string): string | undefined => {
	if (!longitude.trim()) return undefined;

	const value = parseFloat(longitude);
	if (Number.isNaN(value)) return "Must be a valid number";
	if (value < -180 || value > 180)
		return "Longitude must be between -180 and 180";
	return undefined;
};

/**
 * Inline form for renaming a sensor, changing its type, status, or zone, or
 * typing new map coordinates. Changes auto-save after a short pause; Done
 * exits edit.
 */
export const SensorEditForm = ({
	autoSaveStatus = "idle",
	canUndoSensorEdit,
	editDraft,
	editTypeOptions,
	editZoneOptions,
	isEditDirty,
	isUpdatedSensorPending,
	mapViewportCenter = null,
	onCancel,
	onDraftChange,
	onSave,
	onZoneChange,
	zoneMoveHint,
}: SensorEditFormProps) => {
	const latitudeError = getLatitudeError(editDraft.latitude);
	const longitudeError = getLongitudeError(editDraft.longitude);
	const hasCoordinateValues =
		editDraft.latitude.trim() !== "" || editDraft.longitude.trim() !== "";
	const hasMappedCoordinates = isSensorEditDraftCoordinatesValid(editDraft);
	const hasPartialCoordinates = hasCoordinateValues && !hasMappedCoordinates;
	const hasEdited = hasAutoSaveEdits({
		autoSaveStatus,
		canUndoEdit: canUndoSensorEdit,
		isEditDirty,
		isUpdatePending: isUpdatedSensorPending,
	});
	const showDone = autoSaveStatus === "idle" || autoSaveStatus === "saved";

	return (
		<div className="flex min-h-0 flex-1 flex-col overflow-hidden">
			<EditScrollBody>
				<div className="flex flex-col gap-3">
					<Input
						label="Sensor Name"
						onChange={(value) =>
							onDraftChange((current) => ({ ...current, name: value }))
						}
						placeholder="Enter sensor name"
						size="sm"
						value={editDraft.name}
					/>
					<Select
						isRequired
						items={editTypeOptions}
						label="Type"
						onChange={(value) =>
							onDraftChange((current) => ({
								...current,
								type: (value ? String(value) : current.type) as SensorType,
							}))
						}
						placeholder="Select sensor type"
						size="sm"
						value={editDraft.type}
					>
						{(item) => (
							<Select.Item id={item.id} key={item.id}>
								{item.label}
							</Select.Item>
						)}
					</Select>
					<Select
						isRequired
						items={[...SENSOR_STATUS_OPTIONS]}
						label="Status"
						onChange={(value) =>
							onDraftChange((current) => ({
								...current,
								status: (value ? String(value) : "active") as SensorStatus,
							}))
						}
						placeholder="Select status"
						size="sm"
						value={editDraft.status || "active"}
					>
						{(item) => (
							<Select.Item id={item.id} key={item.id}>
								{item.label}
							</Select.Item>
						)}
					</Select>
					<Select
						isDisabled={editZoneOptions.length === 0}
						isRequired
						items={editZoneOptions}
						label="Zone"
						onChange={(value) => onZoneChange(value ? String(value) : "")}
						placeholder={
							editZoneOptions.length === 0
								? "No zones available"
								: "Select a zone"
						}
						size="sm"
						value={editDraft.zoneId || null}
					>
						{(item) => (
							<Select.Item id={item.id} key={item.id}>
								{item.label}
							</Select.Item>
						)}
					</Select>
					{zoneMoveHint ? (
						<p className="text-xs text-tertiary">{zoneMoveHint}</p>
					) : null}
					<div>
						<div className="mb-2 flex items-center justify-between gap-2">
							<p className="text-sm font-medium text-primary">
								Map Coordinates
							</p>
							<div className="flex items-center gap-2">
								<UseMapCenterButton
									onClick={() => {
										if (!mapViewportCenter) return;
										const [lng, lat] = mapViewportCenter;
										onDraftChange((current) => ({
											...current,
											latitude: String(lat),
											longitude: String(lng),
										}));
									}}
									visible={Boolean(mapViewportCenter)}
								/>
								{hasCoordinateValues ? (
									<Tooltip placement="top" title="Reset Long/Lat">
										<Button
											aria-label="Reset Long/Lat"
											color="secondary"
											iconLeading={RefreshCcw05}
											onClick={() =>
												onDraftChange((current) => ({
													...current,
													latitude: "",
													longitude: "",
												}))
											}
											size="sm"
										/>
									</Tooltip>
								) : null}
							</div>
						</div>
						<p className="mb-3 text-xs text-tertiary">
							{hasMappedCoordinates
								? "Drag the pin on the map to move this sensor, or edit the values below. Use Reset to clear coordinates and place the pin again by clicking the map."
								: "Optional. Enter latitude and longitude, or click the map to place this sensor. After it has coordinates, drag the pin to move it."}
						</p>
						<div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
							<Input
								className="w-full"
								errorMessage={latitudeError}
								inputMode="decimal"
								isInvalid={!!latitudeError}
								label="Latitude"
								onChange={(value) =>
									onDraftChange((current) => ({ ...current, latitude: value }))
								}
								placeholder="E.g. 39.7392"
								size="sm"
								type="number"
								value={editDraft.latitude}
							/>
							<Input
								className="w-full"
								errorMessage={longitudeError}
								inputMode="decimal"
								isInvalid={!!longitudeError}
								label="Longitude"
								onChange={(value) =>
									onDraftChange((current) => ({ ...current, longitude: value }))
								}
								placeholder="E.g. -104.9903"
								size="sm"
								type="number"
								value={editDraft.longitude}
							/>
						</div>
						{hasPartialCoordinates ? (
							<p className="mt-2 text-xs text-error-primary">
								Enter both latitude and longitude to map this sensor.
							</p>
						) : null}
					</div>
				</div>
			</EditScrollBody>
			<div className="bg-secondary_subtle flex shrink-0 flex-wrap items-center gap-2 border-t border-secondary px-4 py-3">
				<Button color="secondary" onClick={onCancel} size="sm">
					Cancel
				</Button>
				{hasEdited ? (
					<Button
						color="primary"
						isDisabled={isUpdatedSensorPending || !showDone}
						onClick={onSave}
						size="sm"
					>
						{isUpdatedSensorPending || !showDone ? "Saving..." : "Done"}
					</Button>
				) : null}
			</div>
		</div>
	);
};
